/**
 * AdminConversationList — Linke Spalte im Messenger-Layout
 *
 * Listet alle aktiven Konversationen mit User-Avatar (Initiale),
 * Display-Name, letzter Nachrichten-Vorschau, Zeit, Unread-Badge.
 *
 * Sortierung: aktivste zuerst (Backend liefert bereits korrekt sortiert).
 *
 * Empty-State: dezenter Hinweis wenn keine Konversationen existieren.
 */

import React from 'react';
import type { AdminConversationListItem } from '../../../services/support/adminChatService';
import { getImageUrl } from '../../../utils/imageUtils';

interface AdminConversationListProps {
  conversations: AdminConversationListItem[];
  selectedId: string | null;
  isLoading: boolean;
  onSelect: (conversationId: string) => void;
}

function getInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]![0] ?? '?').toUpperCase();
  return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase();
}

function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = diffMs / 60_000;

  if (diffMin < 1) return 'gerade';
  if (diffMin < 60) return `${Math.floor(diffMin)} Min`;
  if (diffMin < 24 * 60) return `${Math.floor(diffMin / 60)} Std`;

  // Tagesvergleich (Berlin)
  const fmtDay = (d: Date): string =>
    new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(d);
  if (fmtDay(date) === fmtDay(new Date(now.getTime() - 24 * 3600 * 1000))) return 'gestern';

  // Innerhalb 7 Tage: Wochentag-Kurzname
  if (diffMin < 7 * 24 * 60) {
    return new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      weekday: 'short'
    }).format(date);
  }

  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit'
  }).format(date);
}

export function AdminConversationList({
  conversations,
  selectedId,
  isLoading,
  onSelect
}: AdminConversationListProps): React.ReactElement {
  if (isLoading && conversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-[12px] opacity-40 font-mono">Lädt …</p>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center">
        <p className="text-[13px] opacity-50 font-mono">Noch keine Anfragen.</p>
        <p className="text-[11px] opacity-30 font-mono mt-1">
          Sobald Premium-Kunden schreiben, erscheinen sie hier.
        </p>
      </div>
    );
  }

  return (
    <ul className="flex flex-col font-mono">
      {conversations.map((conv) => {
        const isSelected = conv.id === selectedId;
        const hasUnread = conv.unreadCountAdmin > 0;
        return (
          <li key={conv.id}>
            <button
              type="button"
              onClick={() => onSelect(conv.id)}
              className={`
                w-full text-left px-4 py-3
                flex items-start gap-3
                border-b border-white/5 dark:border-white/5
                transition-colors duration-150
                ${isSelected
                  ? 'bg-brand/10 hover:bg-brand/15'
                  : 'hover:bg-white/5 dark:hover:bg-white/5'}
              `}
            >
              {/* Avatar: Bild wenn gesetzt, sonst Initialen-Fallback.
                  Hat-Ungelesen-Badge bekommt der Container in beiden Fällen
                  einen Brand-Ring statt einen Hintergrund. */}
              {conv.userAvatarUrl ? (
                <img
                  src={getImageUrl(conv.userAvatarUrl)}
                  alt=""
                  aria-hidden="true"
                  className={`
                    shrink-0 w-9 h-9 rounded-full object-cover
                    ${hasUnread ? 'ring-2 ring-brand' : ''}
                  `}
                />
              ) : (
                <div
                  className={`
                    shrink-0 w-9 h-9 rounded-full
                    flex items-center justify-center
                    text-[12px] font-semibold
                    ${hasUnread
                      ? 'bg-brand text-gray-950'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}
                  `}
                >
                  {getInitials(conv.userDisplayName)}
                </div>
              )}

              {/* Inhalt */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`text-[13px] truncate ${hasUnread ? 'font-semibold' : ''}`}>
                    {conv.userDisplayName}
                  </span>
                  <span className="text-[10px] opacity-50 shrink-0">
                    {formatRelative(conv.lastMessageAt)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <p
                    className={`
                      text-[12px] truncate min-w-0
                      ${hasUnread ? 'opacity-90' : 'opacity-50'}
                    `}
                  >
                    {conv.lastMessageSenderType === 'admin' && (
                      <span className="opacity-50 mr-1">Du:</span>
                    )}
                    {conv.lastMessagePreview ?? <em className="opacity-40">(leer)</em>}
                  </p>

                  {hasUnread && (
                    <span
                      aria-label={`${conv.unreadCountAdmin} ungelesen`}
                      className="
                        shrink-0
                        bg-brand text-gray-950
                        text-[10px] font-semibold
                        rounded-full px-1.5 min-w-[18px] h-[18px]
                        flex items-center justify-center
                      "
                    >
                      {conv.unreadCountAdmin > 99 ? '99+' : conv.unreadCountAdmin}
                    </span>
                  )}
                </div>

                {/* Plan-Badge dezent */}
                {conv.planSlugAtCreation && (
                  <p className="text-[9px] opacity-30 uppercase tracking-wider mt-1">
                    {conv.planSlugAtCreation}
                  </p>
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
